import React, { useMemo, useRef, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList, ResponsiveContainer } from "recharts";
import { ChartGridType, chartType, ChartViewType, IDynamicChartsProps } from "src/pages/Insights/models/interfaces";
import { chartStyleConfigLine, formatValue, getTextWidth, renderNValueLabel, sortByValuesAndThenByMax, summarizeNSizes } from "src/pages/Insights/transformer";
import { useSelector } from "react-redux";
import { RootState } from "src/store";
import { STYLE_CONSTANTS_CHART } from "src/pages/Insights/transformer";
import { CustomTooltip } from "../../components/CustomTooltip";
import { Empty, Tooltip as TooltipEllips } from "src/assets/imports/antdComponents";
import AxisLabel from "./components/AxisLable";
import { QuantComponentsRenderedFromType } from "src/pages/NewAskKural/models/interfaces";
import useAsyncChartConfig from "src/components/Hooks/useAsyncChartConfig";

const CustomBarChart: React.FC<IDynamicChartsProps> = ({
	wholeData,
	obj,
	type,
	config,
	isMini,
	legends = [],
	showYAxisLabel = true,
	showXAxis = true,
	maxDomain = "auto",
	minDomain = "auto",
	chartSegmentLength = 1,
	isSectionalSummary,
	renderedFrom = QuantComponentsRenderedFromType.Quant,
	viewType = ChartViewType.Dashboard,
	grid = ChartGridType.oneXone,
}) => {
	const [hoveredBarKey, setHoveredBarKey] = useState<string | null>(null);
	const { selectedChart } = useSelector((state: RootState) => state.sectionalSummarySlice.sectionalSummary);
	const { n_size_value = [] } = obj;
	const marginLeftMini = 0;
	const [dynamicPosition, setDynamicPosition] = useState(-300);
	const [isTooltipHovered, setIsTooltipHovered] = useState(false);
	const indexX = useRef(-1);
	const bottomMargin = obj?.xAxisLables ? (obj?.graph_customization?.legend_pos !== "Top" ? 10 : 50) : obj?.graph_customization?.legend_pos !== "Top" ? 50 : 50;
	const data2 =
		legends?.length > 0
			? obj?.chartData?.map((item) => {
					return Object.fromEntries(Object.entries(item).filter(([key]) => !legends.includes(key)));
			  })
			: obj?.chartData;

	// const data =
	// 	type?.toLowerCase() === chartType.Bar?.toLocaleLowerCase() && config.layout === "vertical" ? [...(data2 || [])].sort((a, b) => a.name.localeCompare(b.name)) : data2;

	const data = type?.toLowerCase() === chartType.Bar?.toLocaleLowerCase() && config.layout === "vertical" ? data2 : data2;
	// Calculate number of bars
	const barLength = wholeData?.length;
	const numberOfBars = barLength * (Object.keys(wholeData[0])?.length - 1); // subtracting 1 for 'name'
	const isClusterColumn = obj?.chartType?.toLowerCase() === chartType.Clustered_Column.toLowerCase();
	const isStackedColumn = obj?.chartType?.toLowerCase() === chartType.Stacked_Column.toLowerCase();
	const isStackedBar = obj?.chartType?.toLowerCase() === chartType.Stacked_Bar.toLowerCase();

	const {
		config: customConfig,
		loading: configLoading,
		error: configError,
	} = useAsyncChartConfig(
		obj?.chartType,
		viewType,
		grid,
		obj?.chartData?.length || null,
		numberOfBars || null,
		!!data2 // enabled condition
	);

	const fontCustom = obj?.chartData?.length <= 8 ? STYLE_CONSTANTS_CHART?.yAxisLabelSize : STYLE_CONSTANTS_CHART?.yAxisLabelSize - 1;

	const fontCustomBarTop =
		numberOfBars < 15 ? STYLE_CONSTANTS_CHART?.yAxisLabelSize : numberOfBars < 30 ? STYLE_CONSTANTS_CHART?.yAxisLabelSize - 1 : STYLE_CONSTANTS_CHART?.yAxisLabelSize - 2.5;

	let barKeys: string[] = data ? [...new Set((data as { [key: string]: any }[]).flatMap((obj) => Object.keys(obj).filter((key) => key !== config.dataKeyXAxis)))] : [];

	// Order barKeys according to obj.seriesOrder if it exists
	if (obj?.seriesOrder && Array.isArray(obj.seriesOrder)) {
		const orderedKeys = obj.seriesOrder.filter((key) => barKeys.includes(key));
		// Add any keys that might not be in seriesOrder to the end
		const remainingKeys = barKeys.filter((key) => !obj.seriesOrder.includes(key));
		barKeys = [...orderedKeys, ...remainingKeys];
	}

	if (config.layout === "vertical" && config.stackId) {
		barKeys = barKeys?.reverse();
	}

	const simpleBar = Object.keys(data?.[0] || {}).filter((key) => key !== config.dataKeyXAxis || key !== "n_size");

	const CustomXAxisTickBarTop = (props) => {
		const { x, y, payload } = props;
		const text = payload.value;
		const fontSize = customConfig?.x_axis_top?.tick?.font_size;
		const textWidth = getTextWidth(text, fontSize);
		const padding = customConfig?.x_axis_top?.tick?.padding; // total padding (px-2 = 0.5rem each side = 16px)
		const maxBoxWidth = customConfig?.x_axis_top?.tick?.max_width;
		const totalWidth = Math.min(textWidth + padding, maxBoxWidth) + 10;

		// Ellipsize if text is wider than the box
		const displayText =
			textWidth + padding > maxBoxWidth
				? (() => {
						let truncated = text;
						while (getTextWidth(truncated + "...", fontSize) + padding > maxBoxWidth && truncated.length > 0) {
							truncated = truncated.slice(0, -1);
						}
						return truncated.length < text.length ? truncated + "..." : text;
				  })()
				: text;

		return (
			<g transform={`translate(${x},${y})`}>
				<foreignObject x={-totalWidth / 2} y={type?.toLowerCase() !== chartType.Line_Horizontal?.toLocaleLowerCase() ? -34 : 0} width={totalWidth} height={38}>
					<TooltipEllips title={text} overlayClassName="quant-tooltip-reduced-padding">
						<div className="flex justify-center items-center h-full w-full" style={{ fontWeight: customConfig?.x_axis_top?.tick?.font_weight }}>
							<span
								className={`flex justify-center items-center text-[--secondary-color] bg-[#f9fbfd] ${
									// type?.toLowerCase() !== chartType.Line_Horizontal?.toLocaleLowerCase() ? "border border-[rgba(94,128,183,0.15)] rounded-full  px-5 py-1" : ""
									"border border-[rgba(94,128,183,0.15)] rounded-full  px-5 py-1"
								} `}
								style={{
									fontSize,
									width: `${totalWidth}px`,
									whiteSpace: "nowrap",
									color: customConfig?.x_axis_top?.tick?.label_color,
								}}>
								{displayText}
							</span>
						</div>
					</TooltipEllips>
				</foreignObject>
			</g>
		);
	};

	const CustomXAxisTickBottomVertical = (props, i) => {
		const { x, y, payload, index } = props;
		// const format = (obj.graph_customization?.dlabel_fmt || "##").replace(/\.\#+/, "");
		const format = obj.graph_customization?.dlabel_fmt || "##";
		indexX.current = indexX.current + 1;
		return (
			<g transform={`translate(${x},${y})`}>
				<text
					x={0}
					y={15}
					fill={customConfig?.x_axis_bottom?.tick?.label_color}
					textAnchor={customConfig?.x_axis_bottom?.tick?.text_anchor}
					fontSize={customConfig?.x_axis_bottom?.tick?.font_size}
					fontWeight={customConfig?.x_axis_bottom?.tick?.font_weight}>
					{/* {index === 0 ? summarizeNSizes(n_size_value) : ""} */}
					{formatValue(payload?.value, format)}
				</text>
			</g>
		);
	};

	const CustomXAxisTickLineVertical = (props) => {
		let showToolTip = true;
		const { x, y, payload, allLabels } = props;
		const maxLabelLength = allLabels ? Math.max(...allLabels.map((label) => String(label).length)) : 0;
		const text = payload.value;
		const format = config.layout === "vertical" ? "##" : obj.graph_customization?.dlabel_fmt || "##";

		// Set fixed width to 150px if there are 2 or more chart segments,
		// otherwise use a dynamic width based on label length, with min 100 and max 180,
		// or 20 if isMini is true.
		const maxWidth =
			chartSegmentLength >= 2
				? customConfig?.y_axis_left?.tick?.max_width
				: selectedChart
				? Math.min(Math.max(maxLabelLength * 7 + 10, 100), 150)
				: Math.min(Math.max(maxLabelLength * 7 + 10, 100), customConfig?.y_axis_left?.tick?.max_width);

		const xPosition = isMini ? -10 : selectedChart ? Math.min(-maxWidth - 150, -0) : Math.min(-maxWidth - 80, -0);
		setDynamicPosition(xPosition);

		// Get two-line label with ellipsis if needed
		const getTwoLineText = (str, maxWidth) => {
			if (typeof document === "undefined") return { line1: str, line2: "" };

			const canvas = document.createElement("canvas");
			const ctx = canvas.getContext("2d");
			ctx.font = `${customConfig?.y_axis_left?.tick?.font_size}px Open Sans`;

			// Helper function to break a word if it's too long
			const breakWord = (word, availableWidth) => {
				if (ctx.measureText(word).width + 10 <= availableWidth) {
					return { fittingPart: word, remainingPart: "" };
				}

				let fittingPart = "";
				for (let i = 0; i < word.length; i++) {
					const testPart = fittingPart + word[i];
					if (ctx.measureText(testPart).width + 10 <= availableWidth) {
						fittingPart = testPart;
					} else {
						break;
					}
				}
				return {
					fittingPart: fittingPart,
					remainingPart: word.slice(fittingPart.length),
				};
			};

			let words = String(str)?.split(" ");
			let line1 = "";
			let line2 = "";
			let line1Complete = false;

			for (let i = 0; i < words.length; i++) {
				let currentWord = words[i];

				// Process words for line1
				if (!line1Complete) {
					let testLine1 = line1 + (line1 ? " " : "") + currentWord;
					let testWidth = ctx.measureText(testLine1).width + 10;

					if (testWidth <= maxWidth) {
						line1 = testLine1;
						showToolTip = false;
					} else {
						// Check if the current word alone is larger than maxWidth
						if (ctx.measureText(currentWord).width + 10 > maxWidth) {
							// Word is too large, move to line2 without breaking
							line1Complete = true;
							i--; // Process this word again for line2
						} else {
							// Word fits in maxWidth but not in remaining space of line1
							line1Complete = true;
							i--; // Process this word again for line2
						}
					}
				}
				// Process words for line2
				else {
					// Check if current word is larger than maxWidth
					if (ctx.measureText(currentWord).width + 10 > maxWidth) {
						// Break the word for line2 and add ellipsis
						const { fittingPart } = breakWord(currentWord, maxWidth - ctx.measureText("...").width);
						line2 = line2 + (line2 ? " " : "") + fittingPart + "...";
						showToolTip = true;
						break;
					} else {
						// Normal word processing for line2
						let testLine2 = line2 + (line2 ? " " : "") + currentWord;
						let testWidth = ctx.measureText(testLine2).width + 10;

						if (testWidth <= maxWidth) {
							line2 = testLine2;
						} else {
							// No more space in line2, add ellipsis to previous content
							if (line2) {
								line2 += "...";
							} else {
								// If line2 is empty, try to fit part of current word
								const { fittingPart } = breakWord(currentWord, maxWidth - ctx.measureText("...").width);
								line2 = fittingPart + "...";
							}
							showToolTip = true;
							break;
						}
					}
				}
			}

			// Handle numeric values (unchanged)
			if (!isNaN(payload?.value)) {
				showToolTip = false;
				line1 = formatValue(payload.value, format);
				line2 = "";
			}

			return { line1, line2 };
		};

		const getSingleLineText = (str, maxWidth) => {
			if (typeof document === "undefined") return { line1: str, line2: "" };

			const canvas = document.createElement("canvas");
			const ctx = canvas.getContext("2d");
			ctx.font = `${customConfig?.y_axis_left?.tick?.font_size}px Open Sans`;

			let words = String(str)?.split(" ");
			let line1 = "";
			let line2 = "";

			for (let i = 0; i < words.length; i++) {
				let testLine1 = line1 + (line1 ? " " : "") + words[i];
				if (ctx.measureText(testLine1).width + 10 <= maxWidth) {
					line1 = testLine1;
					showToolTip = false;
				} else {
					line1 = line1 + "...";
					showToolTip = true;
					break;
				}
			}

			const remainingWords = words.slice(line1.split(" ").length);
			if (isNaN(payload?.value)) {
				// for (let i = 0; i < remainingWords.length; i++) {
				// 	let testLine2 = line2 + (line2 ? " " : "") + remainingWords[i];
				// 	if (ctx.measureText(testLine2 + "...").width + 10 <= maxWidth) {
				// 		line2 = testLine2;
				// 	} else {
				// 		line2 += "...";
				// 		break;
				// 	}
				// }
			} else {
				showToolTip = false;
				line1 = formatValue(payload.value, format);
			}

			return { line1, line2: "" };
		};

		let showSingleLineText = true;

		if (isStackedBar) {
			if ((data.length <= 8 && chartSegmentLength === 1) || (data.length <= 6 && chartSegmentLength >= 2)) showSingleLineText = false;
			else showSingleLineText = true;
		}

		const { line1, line2 } = showSingleLineText ? getSingleLineText(text, maxWidth) : getTwoLineText(text, maxWidth);

		const textXPosition = obj?.yAxisLables && !isMini && showYAxisLabel ? (selectedChart ? xPosition + 165 : xPosition + 95) : selectedChart ? xPosition + 150 : xPosition + 80;

		return showToolTip ? (
			<TooltipEllips title={text} overlayClassName="quant-tooltip-reduced-padding">
				<g transform={`translate(${x},${y})`}>
					<text
						x={isMini ? 0 : 0}
						y={0}
						fill={customConfig?.y_axis_left?.tick?.label_color}
						textAnchor={customConfig?.y_axis_left?.tick?.text_anchor}
						fontSize={customConfig?.y_axis_left?.tick?.font_size}
						fontWeight={customConfig?.y_axis_left?.tick?.font_weight}>
						<tspan x={textXPosition} dy="0em" fill={customConfig?.y_axis_left?.tick?.label_color}>
							{line1}
						</tspan>
					</text>
					{line2 && line2.length > 0 && (
						<text
							x={isMini ? 0 : 0}
							y={0 + customConfig?.y_axis_left?.tick?.font_size + 3}
							fill={customConfig?.y_axis_left?.tick?.label_color}
							textAnchor={customConfig?.y_axis_left?.tick?.text_anchor}
							fontSize={customConfig?.y_axis_left?.tick?.font_size}
							fontWeight={customConfig?.y_axis_left?.tick?.font_weight}>
							<tspan x={textXPosition} dy="0em" fill={customConfig?.y_axis_left?.tick?.label_color}>
								{line2}
							</tspan>
						</text>
					)}
				</g>
			</TooltipEllips>
		) : (
			<g transform={`translate(${x},${y})`}>
				<text
					x={isMini ? 0 : 0}
					y={0}
					fill={customConfig?.y_axis_left?.tick?.label_color}
					textAnchor={customConfig?.y_axis_left?.tick?.text_anchor}
					fontSize={customConfig?.y_axis_left?.tick?.font_size}
					fontWeight={customConfig?.y_axis_left?.tick?.font_weight}>
					<tspan x={textXPosition} dy="0em" fill={customConfig?.y_axis_left?.tick?.label_color}>
						{line1}
					</tspan>
				</text>
				{line2 && line2.length > 0 && (
					<text
						x={isMini ? 0 : 0}
						y={0 + customConfig?.y_axis_left?.tick?.font_size + 3}
						fill={customConfig?.y_axis_left?.tick?.label_color}
						textAnchor={customConfig?.y_axis_left?.tick?.text_anchor}
						fontSize={customConfig?.y_axis_left?.tick?.font_size}
						fontWeight={customConfig?.y_axis_left?.tick?.font_weight}>
						<tspan x={textXPosition} dy="0em" fill={customConfig?.y_axis_left?.tick?.label_color}>
							{line2}
						</tspan>
					</text>
				)}
			</g>
		);
	};

	// const CustomYAxisTickRight = (props, i) => {
	// 	const { x, y, payload, index } = props;
	// 	indexX.current = indexX.current + 1;
	// 	const l = n_size_value?.length;

	// 	const h = index == !0 ? 0 : 20;
	// 	const parsed = renderNValueLabel(n_size_value?.[index]);
	// 	return (
	// 		<g transform={`translate(${x},${y})`}>
	// 			{/* Background rectangle for this specific tick */}
	// 			{/* <rect
	// 				x={10}
	// 				// x={type?.toLowerCase() === "bar" ? 30 : 35}
	// 				y={-10 - 2 * h}
	// 				width={100}
	// 				height={index === n_size_value?.length - 1 ? 0 : selectedChart ? 600 / l + h : 300 / l + h}
	// 				// fill="#f5f5f5" // Light red background for each tick
	// 				fill="#fff" // Light red background for each tick
	// 				// rx={index === 0 || index === obj?.chartData?.length - 1 ? 60 : 0} // Round if top or bottom
	// 				// ry={index === 0 || index === obj?.chartData?.length - 1 ? 60 : 0}
	// 			/> */}

	// 			{/* Text displaying the n_size value */}
	// 			<text
	// 				x={type?.toLowerCase() === "bar" ? 75 : 55}
	// 				y={5}
	// 				fill="#d32f2f" // Darker red for text
	// 				textAnchor="middle"
	// 				fontSize="11px"
	// 				fontWeight="600"
	// 				style={{ userSelect: "none" }}>
	// 				{parsed?.labelParts?.map((part, idx) => (
	// 					// <tspan key={idx} fill={part.color}>
	// 					// 	{part.text}
	// 					// </tspan>
	// 					<span key={idx} style={{ color: part?.color }}>
	// 						{part.text}
	// 					</span>
	// 				))}
	// 			</text>
	// 		</g>

	// 		// <g transform={`translate(${x},${y})`}>
	// 		// 	<text
	// 		// 		x={type?.toLowerCase() === "bar" ? 80 : 85}
	// 		// 		y={5}
	// 		// 		fill="#7c9dda"
	// 		// 		textAnchor="middle"
	// 		// 		fontSize={STYLE_CONSTANTS_CHART?.yAxisLabelSize}
	// 		// 		fontWeight="400"
	// 		// 		style={{ color: "red" }}>
	// 		// 		{n_size_value?.[index]}
	// 		// 	</text>
	// 		// </g>
	// 	);
	// };

	const CustomYAxisTickRight = (props) => {
		const { x, y, payload, index } = props;
		indexX.current = indexX.current + 1;
		const l = n_size_value?.length;

		const h = index == !0 ? 0 : 20;
		const parsed = renderNValueLabel(n_size_value?.[index], customConfig?.y_axis_right?.tick?.label_color, customConfig?.y_axis_right?.tick?.label_color_dark);
		// This is the background for each individual tick
		return (
			<g transform={`translate(${x - 40},${y})`}>
				{/* Background rectangle for this specific tick */}
				{/* <rect
						x={10}
						// x={type?.toLowerCase() === "bar" ? 30 : 35}
						y={-10 - 2 * h}
						width={100}
						height={index === n_size_value?.length - 1 ? 0 : selectedChart ? 600 / l + h : 300 / l + h}
						// fill="#f5f5f5" // Light red background for each tick
						fill="#fff" // Light red background for each tick
						// rx={index === 0 || index === obj?.chartData?.length - 1 ? 60 : 0} // Round if top or bottom
						// ry={index === 0 || index === obj?.chartData?.length - 1 ? 60 : 0}
					/> */}

				{/* Text displaying the n_size value */}
				<text
					x={type?.toLowerCase() === "bar" ? 100 : 70}
					y={5}
					fill={customConfig?.y_axis_right?.tick?.label_color_dark} // Darker red for text renderNValueLabel
					textAnchor={customConfig?.y_axis_right?.tick?.text_anchor}
					fontSize={customConfig?.y_axis_right?.tick?.font_size}
					fontWeight={customConfig?.y_axis_right.tick.font_weight}
					style={{ userSelect: "none" }}>
					{parsed?.labelParts?.map((part, idx) => (
						<tspan key={idx} fill={part.color}>
							{part.text}
						</tspan>
					))}
				</text>
			</g>
		);
	};

	const CustomYAxisTick = (props) => {
		let showToolTip = true;
		const { x, y, payload } = props;
		const originalValue = payload.value;
		const format = obj.graph_customization?.dlabel_fmt || "##";
		const formattedValue = formatValue(originalValue, format);
		const maxWidth = customConfig?.y_axis_left?.tick?.max_width;
		let truncatedText = formattedValue;
		const value = formatValue(payload.value, format);
		// const format = (obj.graph_customization?.dlabel_fmt || "##").replace(/\.\#+/, "");
		//const format = "##%";
		const getEllipsisText = (str, maxLength) => {
			// if (isNaN(payload?.value)) {
			let truncatedText = str;
			const canvas = document.createElement("canvas");
			const context = canvas.getContext("2d");
			context.font = `${customConfig?.y_axis_left?.tick?.font_size}px sans-serif`;

			while (context.measureText(truncatedText + "...").width > maxWidth && truncatedText.length > 0) {
				truncatedText = truncatedText.slice(0, -1);
			}
			if (truncatedText.length < str.length) {
				showToolTip = true;
			} else {
				showToolTip = false;
			}

			return truncatedText.length < str.length ? truncatedText + "..." : str;
			// } else {
			// 	showToolTip = false;
			// 	return formatValue(payload.value, format);
			// }
		};

		const displayValue = getEllipsisText(value, maxWidth);

		return (
			<g transform={`translate(${x},${y})`}>
				<foreignObject x={isMini ? 0 : type?.toLowerCase() === chartType.Bar?.toLowerCase() ? -60 : -60} y={-10} width={60} height={20}>
					{/* {truncatedText.length < formattedValue?.length ? ( */}
					{showToolTip ? (
						<TooltipEllips title={formattedValue} overlayClassName="quant-tooltip-reduced-padding">
							<div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
								<span
									style={{
										fontSize: customConfig?.y_axis_left?.tick?.font_size,
										color: customConfig?.y_axis_left?.tick?.label_color,
										fontWeight: customConfig?.y_axis_left?.tick?.font_weight,
									}}>
									{/* {getEllipsisText(formattedValue, maxWidth)} */}
									{displayValue}
								</span>
							</div>
						</TooltipEllips>
					) : (
						<div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
							<span
								style={{
									fontSize: customConfig?.y_axis_left?.tick?.font_size,
									color: customConfig?.y_axis_left?.tick?.label_color,
									fontWeight: customConfig?.y_axis_left?.tick?.font_weight,
								}}>
								{/* {getEllipsisText(formattedValue, maxWidth)} */}
								{displayValue}
							</span>
						</div>
					)}
				</foreignObject>
			</g>
		);
	};

	const renderColoredLabel = (labelParts, maxChars, shouldRotate, chartSegmentLength) => {
		const fullLabelPartsText =
			chartSegmentLength >= 1 && shouldRotate
				? labelParts
						?.map((part) => part.text)
						.join("")
						.replace(/\s+/g, "") || ""
				: labelParts?.map((part) => part.text).join("") || "";

		let truncatedText = fullLabelPartsText;

		if (shouldRotate && fullLabelPartsText.length > maxChars && chartSegmentLength >= 1) {
			truncatedText = fullLabelPartsText.slice(0, maxChars) + "...";
		}

		let currentLength = 0;
		let result = [];

		for (let i = 0; i < labelParts?.length; i++) {
			const { text, color } = labelParts[i];

			if (currentLength >= truncatedText.length) break;

			// Remove spaces from part.text if chartSegmentLength > 1 and shouldRotate
			const processedText = chartSegmentLength >= 1 && shouldRotate ? text.replace(/\s+/g, "") : text;

			const remaining = truncatedText.length - currentLength;
			const visibleText = processedText.slice(0, remaining);

			if (visibleText) {
				result.push(
					<span key={i} style={{ color }}>
						{visibleText}
					</span>
				);
				currentLength += visibleText.length;
			}
		}

		// Append ellipsis if truncation occurred
		if (truncatedText.endsWith("...") && !fullLabelPartsText.endsWith("...")) {
			result.push(<span key="ellipsis">...</span>);
		}

		return result;
	};

	const CustomXAxisTickBottomLine = (props) => {
		let showToolTip = true;
		const isNSizeHidden = customConfig?.x_axis_bottom?.tick?.hide_n_size || false;
		const isNSizeWrapped = customConfig?.x_axis_bottom?.tick?.wrap_n_size ?? true;
		const { x, y, payload, index } = props;
		const parsed = renderNValueLabel(n_size_value?.[index]);
		const nSizeText = parsed?.labelParts?.map((part) => part.text).join("") || "";
		// const tooltipText = payload.value + " (" + n_size_value?.[index] + ")";
		const tooltipText = (
			<span>
				{payload.value} (
				{parsed?.labelParts?.map((part, idx) => (
					<span key={idx} style={{ color: part?.color }}>
						{part.text}
					</span>
				))}
				)
			</span>
		);
		const barCount = props?.barCount || 0;
		const shouldRotate = ((isClusterColumn || isStackedColumn) && barCount > 7) || (chartSegmentLength > 1 && barCount >= 5);
		const text = shouldRotate ? (isNSizeHidden ? payload.value : isNSizeWrapped ? payload.value : payload.value + " (" + nSizeText + ")") : payload.value;
		const fontSize = isMini
			? customConfig?.x_axis_bottom?.tick?.font_size
			: chartSegmentLength > 1 && barCount > 5
			? customConfig?.x_axis_bottom?.tick?.font_size
			: customConfig?.y_axis_left?.tick?.font_size;
		const textWidth = getTextWidth(text, fontSize);
		const padding = customConfig?.x_axis_bottom?.tick?.padding;
		const maxBoxWidth = customConfig?.x_axis_bottom?.tick?.max_width;

		// Determine if we should rotate based on chartSegmentLength
		const maxChars = customConfig?.x_axis_bottom?.tick?.max_char;

		// Calculate width for the text container
		const totalWidth = Math.min(textWidth + padding, maxBoxWidth) + 30;

		// For rotated text, we need to ensure enough width and height
		const rotatedWidth = totalWidth;
		const rotatedHeight = customConfig?.x_axis_bottom?.tick?.rotated_height;

		// Check if n-size label would be truncated
		const nSizeWidth = getTextWidth(`(${nSizeText})`, fontSize);
		const isNSizeTruncated = shouldRotate ? nSizeText.length > maxChars : nSizeWidth + padding > maxBoxWidth;

		// Ellipsize logic - different for rotated vs non-rotated text
		let isTextTruncated = false;

		// Ellipsize logic - different for rotated vs non-rotated text
		const displayText = (() => {
			if (shouldRotate) {
				// For rotated text, limit to 5-6 characters max

				if (text.length > maxChars) {
					isTextTruncated = true;
					return text.slice(0, maxChars).trim() + "...";
				} else {
					return text;
				}
			} else {
				// Original logic for non-rotated text
				if (textWidth + padding > maxBoxWidth) {
					let truncated = text;
					while (getTextWidth(truncated + "...", fontSize) + padding > maxBoxWidth && truncated.length > 0) {
						truncated = truncated.slice(0, -1);
					}
					if (truncated.length < text.length) {
						isTextTruncated = true;
						return truncated.length < text.length ? truncated + "..." : text;
					} else {
						return text;
					}
				} else {
					return text;
				}
			}
		})();

		// Show tooltip if either x-axis text or n-size is truncated
		showToolTip = isTextTruncated || isNSizeTruncated;

		const labelParts = parsed?.labelParts;

		const calculateTransformCoordinates = () => {
			// Base calculations
			const fontSizeMultiplier = fontSize / 12; // Normalize to base font size of 12
			const effectiveTextLength = displayText.length + 3;
			const originalTextLength = text.length;

			// Better logic for minor differences
			const textDifference = originalTextLength - effectiveTextLength;
			const isMinorTruncation = textDifference <= 4; // Minor difference threshold

			if (shouldRotate) {
				let adjustedX, adjustedY;

				if (isNSizeHidden) {
					// For hidden n-size scenario
					adjustedX = x - fontSize * 1.25 - effectiveTextLength * fontSize * 0.02;

					// Y coordinate adjustment - different logic for minor vs major differences
					const baseY = y + fontSize * 1.3 + fontSizeMultiplier * 2;

					let textLengthYOffset;
					if (isMinorTruncation) {
						// For minor differences, use original text length for positioning
						textLengthYOffset = (originalTextLength + 5) * fontSize * 0.06; // Reduced factor
					} else {
						// For major differences, use display text length
						textLengthYOffset = (effectiveTextLength - 5) * fontSize * 0.08;
					}

					adjustedY = baseY + textLengthYOffset;
				} else {
					// Standard rotation scenario
					adjustedX = isNSizeWrapped ? x - 15 : x - fontSize * 0.4 - effectiveTextLength * fontSize * 0.03;

					// Y coordinate adjustment with better logic
					const baseY = y - fontSize * 0.5 + fontSizeMultiplier * 1.5;

					let textLengthYOffset;
					if (isMinorTruncation) {
						// Use original text length for minor differences
						textLengthYOffset = (originalTextLength - 3) * fontSize * 0.04; // Reduced factor
						// Add small compensation for ellipsis
						const ellipsisCompensation = isTextTruncated ? fontSize * 0.15 : 0;
						textLengthYOffset += ellipsisCompensation;
					} else {
						// Use display text length for major differences
						textLengthYOffset = (effectiveTextLength - 3) * fontSize * 0.06;
					}

					adjustedY = baseY + textLengthYOffset;
				}

				// X adjustment with better logic for minor differences
				let textLengthXOffset;
				if (isMinorTruncation) {
					// For minor differences, base on original text length
					textLengthXOffset = (originalTextLength / Math.max(maxChars, 1)) * fontSize * 0.25;
				} else {
					// For major differences, base on display text length
					textLengthXOffset = (effectiveTextLength / Math.max(maxChars, 1)) * fontSize * 0.3;
				}
				adjustedX -= textLengthXOffset;

				// Fine-tune Y position to avoid axis line overlap
				const axisLineAvoidanceOffset = shouldRotate ? fontSize * 0.2 : 0;
				adjustedY += axisLineAvoidanceOffset;

				return { x: adjustedX, y: adjustedY };
			} else {
				// For non-rotated text
				const adjustedX = x;
				const adjustedY = y - 5;

				return { x: adjustedX, y: adjustedY };
			}
		};

		const { x: transformX, y: transformY } = calculateTransformCoordinates();

		const transformValue = shouldRotate
			? `translate(${transformX},${isNSizeWrapped ? transformY + 20 : chartSegmentLength >= 2 && barCount > 6 ? transformY + 15 : transformY + 10}) rotate(${
					customConfig?.x_axis_bottom?.tick?.angel || -45
			  })`
			: `translate(${transformX},${transformY})`;

		const divider = chartSegmentLength === 1 ? 15 : 3.5;

		return (
			<g transform={transformValue}>
				<foreignObject
					// Position adjustments to ensure proper centering with the bar
					x={shouldRotate ? -(rotatedWidth / divider) : -totalWidth + 60 / 2}
					y={shouldRotate ? 5 : 5}
					width={shouldRotate ? rotatedWidth + 25 : totalWidth + 25}
					height={shouldRotate ? rotatedHeight : 38}>
					{showToolTip ? (
						<TooltipEllips placement={"bottom"} title={tooltipText} overlayClassName="quant-tooltip-reduced-padding">
							<div
								className="flex flex-col h-full w-full font-semibold"
								style={
									shouldRotate
										? {
												// transform: `rotate(${customConfig?.x_axis_bottom?.tick?.angel}deg)`,
												// transformOrigin: "20% 10%", // Changed to better align with bar
												// paddingTop: "5px",
												// marginLeft: "-1px",
										  }
										: {
												display: "flex",
												flexDirection: "column",
												justifyContent: "center",
												alignItems: "center",
										  }
								}>
								<span
									className={`text-[${customConfig?.x_axis_bottom?.tick?.label_color}]`}
									style={{
										fontSize,
										whiteSpace: "nowrap",
										display: "block", // Ensures text doesn't get cut off
										textAlign: shouldRotate ? "left" : "center",
										width: shouldRotate ? "auto" : `${totalWidth}px`,
									}}>
									{displayText}
								</span>
								{!isNSizeHidden && isNSizeWrapped && (
									<span
										className={`text-[${customConfig?.x_axis_bottom?.tick?.label_color}]`}
										style={{
											color: customConfig?.x_axis_bottom?.tick?.label_color,
											fontSize: fontSize,
											display: "block", // Ensures text doesn't get cut off
											marginTop: shouldRotate ? "-2px" : undefined,
											marginLeft: 1,
										}}>
										({renderColoredLabel(labelParts, maxChars, shouldRotate, chartSegmentLength)})
									</span>
								)}
							</div>
						</TooltipEllips>
					) : (
						<div
							className="flex flex-col h-full w-full font-semibold"
							style={
								shouldRotate
									? {
											// transform: `rotate(${customConfig?.x_axis_bottom?.tick?.angel}deg)`,
											// transformOrigin: "30% 24%", // Changed to better align with bar
											// paddingTop: "5px",
											// marginLeft: "-5px",
									  }
									: {
											display: "flex",
											flexDirection: "column",
											justifyContent: "center",
											alignItems: "center",
									  }
							}>
							<span
								className={`text-[${customConfig?.x_axis_bottom?.tick?.label_color}]`}
								style={{
									fontSize,
									whiteSpace: "nowrap",
									display: "block", // Ensures text doesn't get cut off
									textAlign: shouldRotate ? "left" : "center",
									width: shouldRotate ? "auto" : `${totalWidth}px`,
									marginLeft: shouldRotate ? "4px" : undefined,
								}}>
								{displayText}
							</span>
							{!isNSizeHidden && isNSizeWrapped && (
								<span
									className={`text-[${customConfig?.x_axis_bottom?.tick?.label_color}]`}
									style={{
										color: customConfig?.x_axis_bottom?.tick?.label_color,
										fontSize: fontSize,
										display: "block", // Ensures text doesn't get cut off
										marginTop: shouldRotate ? "-2px" : undefined,
										marginLeft: shouldRotate ? "4px" : undefined,
									}}>
									({renderColoredLabel(labelParts, maxChars, shouldRotate, chartSegmentLength)})
								</span>
							)}
						</div>
					)}
				</foreignObject>
			</g>
		);
	};

	function getLeftPosition() {
		const m = customConfig?.margin?.left;

		// if (isMini) return marginLeftMini;
		if (config.layout === "vertical" && !isMini) {
			if (!showYAxisLabel) return m?.vertical.no_y_axis_label;

			// then apply dynamic offset
			const offsetKey = selectedChart ? "selected_chart" : "default";
			return -dynamicPosition - m?.vertical.dynamic_offset[offsetKey];
		}
		return m?.default;
	}

	function getBottomPosition() {
		const legendPosition = obj?.graph_customization?.legend_pos;

		if (isSectionalSummary) return 0;

		// Special handling for stacked bar charts
		if (config.stackId && chartSegmentLength > 3) {
			if (legendPosition === "Top") {
				return config.layout === "vertical" ? 10 : 20;
			}
			// Reduce bottom margin for stacked charts
			return config.layout === "vertical" ? 10 : 15;
		}

		if (legendPosition === "Top") {
			if (barKeys?.length === 1 && config.layout === "vertical") return 10;
			if (barKeys?.length === 1 && config.layout === "horizontal") return 20;
			return obj?.xAxisLables ? 80 : 60;
		}
		if (config.layout !== "vertical" && renderedFrom === QuantComponentsRenderedFromType.Widget) return 5;
		if (config.layout !== "vertical" && renderedFrom === QuantComponentsRenderedFromType.Quant) return 20;
		if (barKeys?.length === 1 && config.layout === "vertical") return 10;
		return 80 - bottomMargin;
	}

	const calculateBarSize = () => {
		if (isMini) {
			if (config?.layout === "horizontal") {
				return 20;
			} else if (config?.layout === "vertical") {
				return 25;
			}
		} else if (config?.stackId) {
			if (config?.layout === "horizontal") {
				return Math.min(75, config.barSize);
			} else if (config?.layout === "vertical") {
				return Math.min(30, config.barSize);
			}
		} else {
			if (config?.layout === "horizontal") {
				return Math.min(75, config.barSize);
			} else if (config?.layout === "vertical") {
				return Math.min(30, config.barSize);
			} else {
				return Math.min(40, config.barSize);
			}
		}
	};

	if (!data2 || configError || configLoading)
		return (
			<div className="flex justify-center h-full align-middle empty-center">
				<Empty />
			</div>
		);

	return (
		<>
			<div className="flex">
				{obj?.yAxisLables && !isMini && showYAxisLabel && (
					// <div className="flex justify-center items-center max-w-4" style={{ paddingBottom: config.height / 3 }}>
					<div className="flex justify-center items-center max-w-4" style={{ paddingBottom: "50px" }}>
						<AxisLabel title={obj?.yAxisLables} type="y" config={customConfig?.y_axis_label} />
					</div>
				)}

				<ResponsiveContainer height={config.height ?? 250}>
					<BarChart
						data={data}
						layout={config.layout} //config.layout === "vertical"
						barSize={customConfig?.bar_size?.size || config.barSize}
						// barGap={customConfig?.bar_gap}
						width={config.width}
						height={config.height}
						margin={{
							top: customConfig?.margin?.top,
							left: getLeftPosition(),
							bottom: getBottomPosition(),
							right: customConfig?.margin?.right,
						}}>
						{/* Grid */}
						<CartesianGrid
							stroke={customConfig?.cartesian_grid?.stroke}
							strokeDasharray={customConfig?.cartesian_grid?.stroke_dasharray}
							horizontal={config?.h_grid?.toLocaleLowerCase() === "yes" ? true : customConfig?.cartesian_grid?.show_h_grid}
							vertical={config?.v_grid?.toLocaleLowerCase() === "yes" ? true : customConfig?.cartesian_grid?.show_v_grid}
						/>

						{/* XAxis / YAxis swap based on layout */}

						{config.layout === "vertical" ? (
							<>
								<XAxis
									type="number"
									hide={isMini || obj.graph_customization?.x_vis?.toLowerCase() !== "yes"}
									//hide={true}
									// axisLine={false}
									tickLine={customConfig?.x_axis_bottom?.show_tick_line}
									domain={[0, maxDomain]}
									tick={<CustomXAxisTickBottomVertical />}
									// label={{
									// 	value: isMini ? obj?.xAxisLables : obj?.xAxisLables,
									// 	position: "bottom",
									// 	offset: 20,
									// }}
									stroke={customConfig?.x_axis_bottom?.stroke}
									strokeWidth={customConfig?.x_axis_bottom?.stroke_width}
								/>

								<YAxis
									type="category"
									dataKey={config.dataKeyXAxis}
									domain={[0, maxDomain]}
									hide={!showYAxisLabel || isMini || obj.graph_customization?.y_vis?.toLowerCase() !== "yes"}
									// tick={<CustomXAxisTickBarVertical />}
									//axisLine={false}
									tick={(props) => (
										<CustomXAxisTickLineVertical
											{...props}
											allLabels={data.map((item) => item?.name)}
											isMini={isMini} // if you're using isMini
										/>
									)} //
									tickLine={customConfig?.y_axis_left?.show_tick_line}
									stroke={customConfig?.y_axis_left?.stroke}
									strokeWidth={customConfig?.y_axis_left?.stroke_width}
									axisLine={customConfig?.y_axis_left?.show_axis_line}
									interval={customConfig?.y_axis_left?.interval}
								/>

								{/* {barKeys?.length === 1 && ( */}
								<YAxis
									dataKey={config.dataKeyXAxis}
									padding={{ bottom: customConfig?.y_axis_right?.padding?.bottom }}
									yAxisId="right"
									orientation="right"
									type="category" // Should be category
									//hide={isMini || obj.graph_customization?.y_vis?.toLocaleLowerCase() !== "yes" || barKeys?.length === 1}
									hide={isMini}
									tickLine={customConfig?.y_axis_right?.show_tick_line}
									axisLine={customConfig?.y_axis_right?.show_axis_line}
									tick={<CustomYAxisTickRight />}
									interval={customConfig?.y_axis_right?.interval}
									stroke={customConfig?.y_axis_right?.stroke}
									strokeWidth={customConfig?.y_axis_right?.stroke_width}
									dx={100}
								/>
								{/* )} */}
							</>
						) : (
							<>
								<XAxis
									padding={{ left: customConfig?.x_axis_top?.padding?.left, right: customConfig?.x_axis_top?.padding?.right }}
									xAxisId="top"
									type="category"
									dataKey={config.dataKeyXAxis}
									//hide={isMini || obj.graph_customization?.x_vis?.toLowerCase() !== "yes"}
									//hide={obj.graph_customization?.x_vis?.toLowerCase() !== "yes"}
									hide={customConfig?.x_axis_top?.is_hidden}
									orientation="top"
									axisLine={customConfig?.x_axis_top?.show_axis_line}
									tickLine={customConfig?.x_axis_top?.show_tick_line}
									// tick={<CustomXAxisTickBarTop />}
									tick={(props) => (
										<CustomXAxisTickBarTop
											{...props}
											isMini={isMini}
											yAxisLableSize={fontCustom}
											yAxisLablecolor={STYLE_CONSTANTS_CHART?.yAxisLabelColor}
											type={type}
										/>
									)}
									interval={customConfig?.x_axis_top?.interval}
									stroke={customConfig?.x_axis_top?.stroke}
									strokeWidth={customConfig?.x_axis_top?.stroke_width}
								/>
								<XAxis
									padding={{ left: customConfig?.x_axis_bottom?.padding?.left, right: customConfig?.x_axis_bottom?.padding?.right }}
									type="category"
									xAxisId="bottom"
									tickLine={customConfig?.x_axis_bottom?.show_tick_line}
									axisLine={customConfig?.x_axis_bottom?.show_axis_line}
									orientation="bottom"
									dataKey={config.dataKeyXAxis}
									hide={isMini || obj.graph_customization?.x_vis?.toLowerCase() !== "yes"}
									//tick={<CustomXAxisTickBottom />}
									tick={<CustomXAxisTickBottomLine barCount={data?.length} />}
									interval={customConfig?.x_axis_bottom?.interval}
									stroke={customConfig?.x_axis_bottom?.stroke}
									strokeWidth={customConfig?.x_axis_bottom?.stroke_width}
									domain={[0, maxDomain]}
									height={customConfig?.x_axis_bottom?.height || 40}
								/>
								<YAxis
									dataKey={config.dataKeyYAxis}
									tick={<CustomYAxisTick />}
									type={config.YAxisType}
									hide={isMini || !showYAxisLabel || obj.graph_customization?.y_vis?.toLowerCase() !== "yes"}
									tickLine={customConfig?.y_axis_left?.show_tick_line}
									domain={[0, maxDomain]}
									stroke={customConfig?.y_axis_left?.stroke}
									strokeWidth={customConfig?.y_axis_left?.stroke_width}
									interval={customConfig?.y_axis_left?.interval}
								/>
							</>
						)}

						{/* Tooltip */}
						{config.showTooltip && !isMini && (
							<Tooltip
								content={
									<CustomTooltip
										chartDataTooltip={obj?.chartDataTooltip}
										graphCustomization={obj?.graph_customization}
										setIsTooltipHovered={setIsTooltipHovered}
										type={"default"}
										chartTypeTooltip={type}
										obj={obj}
										config={config}
										hoveredBarKey={hoveredBarKey}
										customConfig={customConfig}
									/>
								}
								cursor={{ fill: customConfig?.tooltip?.color }}
							/>
						)}

						{/* Legend */}
						{config.showLegends && <Legend />}

						{/* Bars */}
						{(config?.type !== "simplebar" ? (config.stackId ? barKeys?.reverse() : barKeys) : simpleBar)?.map((key, index) => {
							return (
								<Bar
									key={key}
									dataKey={key}
									fill={obj?.colorLegends?.[key]}
									stackId={config.stackId}
									fillOpacity={1}
									{...(config.layout !== "vertical" && {
										xAxisId: config.xOrientation || "top",
									})}
									onMouseMove={() => {
										setHoveredBarKey(key);
									}}
									// onMouseLeave={() => setHoveredBarKey(null)}
								>
									{config?.show_dlabels && !isMini && (
										<LabelList
											dataKey={key}
											onMouseMove={() => setHoveredBarKey(key)}
											// onMouseLeave={() => setHoveredBarKey(null)}
											fill={
												config.stackId
													? // config.layout === "vertical" &&
													  barKeys?.length === 1
														? obj?.colorLegends?.[key]
														: "#fff"
													: obj?.colorLegends?.[key]
											}
											position={
												config.stackId
													? config.layout === "vertical" && barKeys?.length === 1
														? "right"
														: config.layout === "horizontal" && barKeys?.length === 1
														? "top"
														: customConfig?.resolved_data_label?.position
													: customConfig?.resolved_data_label?.position
											}
											formatter={(value) => {
												const format = obj.graph_customization?.dlabel_fmt || "##";
												const valueFormated = formatValue(Number(value), format);
												return valueFormated;
											}}
											style={{
												fontSize: isSectionalSummary ? customConfig?.resolved_data_label?.font_size - 1 : customConfig?.resolved_data_label?.font_size,
											}}
										/>
									)}
								</Bar>
							);
						})}
					</BarChart>
				</ResponsiveContainer>
			</div>
			{obj?.xAxisLables && !isMini && (
				<div className="flex justify-center items-center max-h-4 mt-2">
					<AxisLabel title={obj?.xAxisLables} type="x" config={customConfig?.x_axis_label} />
				</div>
			)}
		</>
	);
};
export default CustomBarChart;
